import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, X, Building2, Users, Eye, EyeOff, MapPin, Edit3, Trash2, ShieldCheck, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { userCan } from "@/lib/permissions";

export default function AdminFranquicias() {
  const { user: me } = useAuth();
  const canCreate = userCan(me, "M14", "create");
  const canEdit = userCan(me, "M14", "edit");
  const canDelete = userCan(me, "M14", "delete");
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/franchises"); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (f) => {
    if (f.is_headquarters) { toast.error("La sede principal no puede eliminarse"); return; }
    if (!confirm(`¿Eliminar la franquicia "${f.name}"?\nDebe estar vacía de usuarios.`)) return;
    try {
      await api.delete(`/franchises/${f.franchise_id}`);
      toast.success("Franquicia eliminada");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-franquicias-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#F5B82E]">Gestión M14</p>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#1F2547]">Franquicias</h1>
          <p className="text-sm text-[#6E7591] mt-1">Sede Principal + franquicias operativas. Cada franquicia opera en su propio espacio aislado (multi-tenant).</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="jasq-btn-primary inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="open-create-franchise"
          disabled={!canCreate}
          title={canCreate ? "" : "Tu perfil no permite crear franquicias"}
        >
          <Plus size={16} /> Crear franquicia
        </button>
      </div>

      {loading ? (
        <div className="jasq-card p-10 text-center text-sm text-[#6E7591]">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="jasq-card p-10 text-center">
          <Building2 size={32} className="mx-auto text-[#F5B82E] mb-2" />
          <p className="font-bold text-[#1F2547]">Aún no hay franquicias</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((f) => (
            <div key={f.franchise_id} className={`jasq-card p-5 ${f.is_headquarters ? "ring-2 ring-[#F5B82E]" : ""}`} data-testid={`franchise-${f.franchise_id}`}>
              <div className="flex items-start gap-3 mb-3">
                <span className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${f.is_headquarters ? "bg-[#F5B82E]/25 text-[#F5B82E]" : "bg-[#F5B82E]/15 text-[#F5B82E]"}`}>
                  {f.is_headquarters ? <ShieldCheck size={20} /> : <Building2 size={20} />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-display font-extrabold text-[#1F2547] truncate">{f.name}</p>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-[#6E7591] truncate">
                    {f.is_headquarters ? "Sede Principal · matriz" : f.franchise_id}
                  </p>
                </div>
                <span className={`shrink-0 jasq-pill ${f.status === "active" ? "bg-[#E7F4E9] text-[#2A7A36]" : "bg-[#FFE9F0] text-[#A52258]"}`}>
                  {f.status === "active" ? "Activo" : f.status === "suspended" ? "Suspendido" : f.status === "inactive" ? "Inactivo" : f.status}
                </span>
              </div>
              {(f.city || f.country) && (
                <p className="text-sm text-[#4C5478] inline-flex items-center gap-1.5"><MapPin size={13} /> {[f.city, f.country].filter(Boolean).join(", ")}</p>
              )}
              {f.owner && !f.is_headquarters && (
                <div className="mt-3 pt-3 border-t border-[#E1E5F0] flex items-center gap-3">
                  <img src={f.owner.avatar} alt="" className="w-8 h-8 rounded-full border-2 border-[#E1E5F0]" />
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-[#1F2547] truncate">{f.owner.name}</p>
                    <p className="text-[11px] text-[#6E7591] truncate">{f.owner.email}</p>
                  </div>
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-[#E1E5F0] flex flex-wrap items-center gap-2">
                <span className="jasq-pill bg-[#F2F4FA] text-[#2C49A0]"><Users size={12} /> {f.users_count}</span>
                <div className="flex-1 min-w-0" />
                <button
                  onClick={() => setEditing(f)}
                  data-testid={`edit-franchise-${f.franchise_id}`}
                  className="jasq-btn-ghost inline-flex items-center gap-1.5 text-xs py-1.5 px-3 disabled:opacity-40 disabled:cursor-not-allowed"
                  disabled={!canEdit}
                  title={canEdit ? "" : "Tu perfil no permite editar franquicias"}
                ><Edit3 size={13} /> Editar</button>
                {!f.is_headquarters && canDelete && (
                  <button
                    onClick={() => onDelete(f)}
                    data-testid={`delete-franchise-${f.franchise_id}`}
                    className="text-[#A52258] hover:underline text-xs font-bold inline-flex items-center gap-1"
                  ><Trash2 size={13} /> Eliminar</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && <FranchiseCreateWizard onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}
      {editing && <FranchiseEditModal franchise={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function FranchiseEditModal({ franchise, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: franchise.name || "",
    city: franchise.city || "",
    country: franchise.country || "",
    status: franchise.status || "active",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/franchises/${franchise.franchise_id}`, form);
      toast.success("Franquicia actualizada");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1F2547]/50 backdrop-blur-sm flex justify-end" data-testid="franchise-edit-modal">
      <div className="jasq-drawer-panel bg-white shadow-2xl w-full max-w-md h-full overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E1E5F0]">
          <div>
            <p className="text-[11px] tracking-wider uppercase font-extrabold text-[#F5B82E]">Edición</p>
            <h3 className="font-display text-xl font-extrabold text-[#1F2547]">{franchise.name}</h3>
          </div>
          <button onClick={onClose} className="text-[#6E7591] hover:text-[#1F2547]" data-testid="fedit-close"><X size={20} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <Field label="Nombre"><input data-testid="fedit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] font-medium" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ciudad"><input data-testid="fedit-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] font-medium" /></Field>
            <Field label="País"><input data-testid="fedit-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] font-medium" /></Field>
          </div>
          {!franchise.is_headquarters && (
            <Field label="Estado">
              <div className="flex gap-2">
                {["active", "suspended"].map((s) => (
                  <button key={s} type="button" onClick={() => setForm({ ...form, status: s })}
                    data-testid={`fedit-status-${s}`}
                    className={`flex-1 px-3 py-2 rounded-xl border-2 text-sm font-bold ${form.status === s ? "border-[#1F2547] bg-[#1F2547] text-white" : "border-[#E1E5F0] hover:border-[#F5B82E]"}`}>
                    {s === "active" ? "Activa" : "Suspendida"}
                  </button>
                ))}
              </div>
            </Field>
          )}
        </div>
        <div className="px-6 py-4 border-t border-[#E1E5F0] bg-[#F7F8FA] flex items-center gap-3">
          <button onClick={onClose} className="jasq-btn-ghost">Cancelar</button>
          <div className="flex-1" />
          <button onClick={save} disabled={saving} className="jasq-btn-primary" data-testid="fedit-submit">
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FranchiseCreateWizard({ onClose, onSaved }) {
  const [step, setStep] = useState(1);
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", city: "", country: "Ecuador",
    owner_name: "", owner_email: "", owner_password: "", owner_phone: "",
    logo_url: "",
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const onPickLogo = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Máx. 4 MB"); return; }
    setUploadingLogo(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/franchises/upload-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, logo_url: data.url }));
      toast.success("Imagen cargada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cargar la imagen");
    } finally { setUploadingLogo(false); }
  };

  const canStep1 = form.name.length >= 3;
  const canStep2 = form.owner_name.length >= 2 && /.+@.+\..+/.test(form.owner_email) && form.owner_password.length >= 6;

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/franchises", form);
      toast.success("Franquicia creada");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al crear franquicia");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1F2547]/50 backdrop-blur-sm flex justify-end" data-testid="franchise-wizard-modal">
      <div className="jasq-drawer-panel bg-white shadow-2xl w-full max-w-lg h-full overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E1E5F0]">
          <div>
            <p className="text-[11px] tracking-wider uppercase font-extrabold text-[#F5B82E]">Asistente · paso {step}/3</p>
            <h3 className="font-display text-xl font-extrabold text-[#1F2547]">Crear franquicia + titular</h3>
          </div>
          <button onClick={onClose} className="text-[#6E7591] hover:text-[#1F2547]" data-testid="fwizard-close"><X size={20} /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {step === 1 && (
            <>
              <Field label="Nombre de la franquicia">
                <input data-testid="fwizard-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] font-medium" placeholder="Ej. Franquicia Norte Quito" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Ciudad">
                  <input data-testid="fwizard-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] font-medium" />
                </Field>
                <Field label="País">
                  <input data-testid="fwizard-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] font-medium" />
                </Field>
              </div>
              <Field label="Imagen de la sede (opcional)">
                <div className="flex items-center gap-3">
                  {form.logo_url ? (
                    <img src={form.logo_url} alt="logo" className="w-16 h-16 rounded-xl border-2 border-[#E1E5F0] object-cover" data-testid="fwizard-logo-preview" />
                  ) : (
                    <div className="w-16 h-16 rounded-xl border-2 border-dashed border-[#E1E5F0] flex items-center justify-center text-[#A6ACC1]"><Building2 size={20} /></div>
                  )}
                  <div className="flex-1">
                    <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-[#E1E5F0] hover:border-[#2C49A0] cursor-pointer text-sm font-bold text-[#1F2547]" data-testid="fwizard-logo-pick">
                      <ImageIcon size={14} /> {uploadingLogo ? "Cargando…" : (form.logo_url ? "Cambiar imagen" : "Subir imagen")}
                      <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                        onChange={(e) => onPickLogo(e.target.files?.[0])} disabled={uploadingLogo} />
                    </label>
                    {form.logo_url && (
                      <button type="button" onClick={() => setForm({ ...form, logo_url: "" })} className="ml-2 text-xs text-[#A52258] font-bold" data-testid="fwizard-logo-clear">Quitar</button>
                    )}
                    <p className="text-[11px] text-[#6E7591] mt-1">JPG / PNG / WebP · Máx. 4 MB. Se mostrará en la cabecera de la sede.</p>
                  </div>
                </div>
              </Field>
            </>
          )}
          {step === 2 && (
            <>
              <Field label="Nombre del titular (Franquiciado)">
                <input data-testid="fwizard-owner-name" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] font-medium" />
              </Field>
              <Field label="Correo del titular">
                <input data-testid="fwizard-owner-email" type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] font-medium" />
              </Field>
              <Field label="Contraseña (mín 6 caracteres)">
                <div className="relative">
                  <input data-testid="fwizard-owner-pwd" type={showPwd ? "text" : "password"} value={form.owner_password}
                    onChange={(e) => setForm({ ...form, owner_password: e.target.value })}
                    className="w-full px-4 py-3 pr-12 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] font-medium" />
                  <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6E7591]" tabIndex={-1}>
                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </Field>
              <Field label="Teléfono (opcional)">
                <input data-testid="fwizard-owner-phone" value={form.owner_phone} onChange={(e) => setForm({ ...form, owner_phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] font-medium" />
              </Field>
            </>
          )}
          {step === 3 && (
            <div className="space-y-3">
              <h4 className="font-display font-extrabold text-[#1F2547]">Confirmación</h4>
              <div className="jasq-card p-4 space-y-1.5 text-sm">
                <RowKV k="Franquicia" v={form.name} />
                <RowKV k="Ciudad" v={[form.city, form.country].filter(Boolean).join(", ") || "—"} />
                <RowKV k="Titular" v={form.owner_name} />
                <RowKV k="Email titular" v={form.owner_email} />
                {form.owner_phone && <RowKV k="Teléfono" v={form.owner_phone} />}
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-[#E1E5F0] bg-[#F7F8FA] flex items-center gap-3">
          <button onClick={onClose} className="jasq-btn-ghost">Cancelar</button>
          <div className="flex-1" />
          {step > 1 && <button onClick={() => setStep((s) => s - 1)} className="jasq-btn-ghost" data-testid="fwizard-back">Atrás</button>}
          {step < 3 && (
            <button
              onClick={() => setStep((s) => s + 1)}
              className="jasq-btn-blue disabled:opacity-50"
              disabled={(step === 1 && !canStep1) || (step === 2 && !canStep2)}
              data-testid="fwizard-next"
            >Siguiente</button>
          )}
          {step === 3 && (
            <button onClick={submit} disabled={submitting} className="jasq-btn-primary" data-testid="fwizard-submit">
              {submitting ? "Creando…" : "Crear franquicia"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] tracking-wider uppercase font-extrabold text-[#6E7591]">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
function RowKV({ k, v }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-[11px] uppercase tracking-wider font-bold text-[#6E7591] min-w-[120px]">{k}</span>
      <span className="font-bold text-[#1F2547] break-all">{v || "—"}</span>
    </div>
  );
}
